/**
 * 前端日志上报工具
 *
 * 对接后端两个接口：
 *   1. POST /api/log/ticket  — 用 bodyHash 换取短时 ticket
 *   2. POST /api/log         — 携带 ticket 正式上报日志
 *
 * baseUrl 默认取当前页面 origin，也可在配置中指定任意上报域名。
 *
 * ── 使用示例 ────────────────────────────────────────────────────────────────
 *
 *   // 方式一：全局配置后调用
 *   window.__FRONTEND_LOG_CONFIG__ = {
 *     appId: 'us-login',          // 应用标识，必填，max 64
 *     baseUrl: '',                // API 根地址，空则取当前页面 origin
 *     debug: false,               // true 时在控制台输出调试信息
 *   };
 *   FrontendLogReporter.report({
 *     errorType: 'LoggerError',
 *     errorMessage: 'Request failed with status code 403',
 *     stack: error.stack,
 *   });
 *
 *   // 方式二：创建独立实例
 *   var reporter = FrontendLogReporter.create({ appId: 'eu-login' });
 *   reporter.reportError(new Error('something wrong'));
 *
 *   // 方式三：自动捕获未处理异常（可选）
 *   FrontendLogReporter.installGlobalHandlers({ appId: 'us-login' });
 *
 * ── 上报流程 ────────────────────────────────────────────────────────────────
 *
 *   准备日志参数 → 计算 bodyHash → 申请 ticket → 携带 ticket 上报
 *   前端不保存固定 secret；bodyHash 参与 ticket 签发，服务端重新验签。
 */
(function (global) {
  'use strict';

  /** 标准请求体字段（参与 bodyHash 的 8 项 + 不参与哈希的 stack） */
  var STANDARD_PAYLOAD_KEYS = {
    appId: true,
    pageUrl: true,
    errorType: true,
    errorMessage: true,
    stack: true,
    userAgent: true,
    deviceInfo: true,
    timestamp: true,
    retryCount: true,
  };

  /** 各字段最大长度（与接口文档一致） */
  var FIELD_LIMITS = {
    appId: 64,
    pageUrl: 1000,
    errorType: 64,
    errorMessage: 2000,
    stack: 8000,
    userAgent: 1000,
    deviceInfo: 255,
  };

  /** 默认从 window.__FRONTEND_LOG_CONFIG__ 读取的全局配置 */
  var DEFAULT_CONFIG = global.__FRONTEND_LOG_CONFIG__ || {};

  // ── 工具函数 ──────────────────────────────────────────────────────────────

  /**
   * 截断字符串到指定最大长度（接口对多个字段有 max 限制）
   * @param {string} value
   * @param {number} maxLen
   * @returns {string}
   */
  function truncate(value, maxLen) {
    var str = value == null ? '' : String(value);
    if (str.length <= maxLen) return str;
    return str.slice(0, maxLen);
  }

  /**
   * 将数值限制在 [min, max] 区间（retryCount 允许 0–10）
   * @param {*} value
   * @param {number} min
   * @param {number} max
   * @param {number} fallback
   * @returns {number}
   */
  function clampInt(value, min, max, fallback) {
    var num = Number(value);
    if (!Number.isFinite(num)) num = fallback;
    num = Math.floor(num);
    if (num < min) return min;
    if (num > max) return max;
    return num;
  }

  /**
   * 生成本次上报请求的唯一随机串（X-Log-Nonce）
   * nonce 5 分钟内只能使用一次，用于防重放
   * @returns {string}
   */
  function generateNonce() {
    if (global.crypto && typeof global.crypto.randomUUID === 'function') {
      return global.crypto.randomUUID();
    }
    return (
      Date.now().toString(36) +
      '-' +
      Math.random().toString(36).slice(2) +
      '-' +
      Math.random().toString(36).slice(2)
    );
  }

  /**
   * SHA-256 哈希，输出 64 位小写十六进制（bodyHash 格式）
   * @param {string} text
   * @returns {Promise<string>}
   */
  function sha256Hex(text) {
    if (!global.crypto || !global.crypto.subtle) {
      return Promise.reject(new Error('Web Crypto API unavailable'));
    }
    var encoder = new TextEncoder();
    return global.crypto.subtle
      .digest('SHA-256', encoder.encode(text))
      .then(function (buffer) {
        return Array.from(new Uint8Array(buffer))
          .map(function (b) {
            return b.toString(16).padStart(2, '0');
          })
          .join('');
      });
  }

  /**
   * 读取设备信息字符串（viewport 宽高，写入 deviceInfo 字段）
   * 格式示例："412x915"
   * @returns {string}
   */
  function getDeviceInfo() {
    var w = global.innerWidth || 0;
    var h = global.innerHeight || 0;
    return w + 'x' + h;
  }

  /**
   * 解析 API 根地址（baseUrl 为空时使用当前页面 origin）
   * @param {string} baseUrl
   * @returns {string}
   */
  function resolveBaseUrl(baseUrl) {
    if (baseUrl) return String(baseUrl).replace(/\/+$/, '');
    try {
      return global.location.origin;
    } catch (e) {
      return '';
    }
  }

  /**
   * 构造参与 bodyHash 计算的 canonical JSON 字符串
   *
   * 参与哈希的字段（顺序固定，stack 不参与）：
   *   appId, pageUrl, errorType, errorMessage, userAgent, deviceInfo, timestamp, retryCount
   * 缺失字段按空串或 0 补齐，算法：sha256(JSON.stringify(canonicalPayload))
   *
   * @param {Object} payload 已规范化的日志体
   * @returns {string}
   */
  function buildCanonicalPayloadString(payload) {
    return JSON.stringify({
      appId: payload.appId || '',
      pageUrl: payload.pageUrl || '',
      errorType: payload.errorType || '',
      errorMessage: payload.errorMessage || '',
      userAgent: payload.userAgent || '',
      deviceInfo: payload.deviceInfo || '',
      timestamp: Number(payload.timestamp || 0),
      retryCount: Number(payload.retryCount || 0),
    });
  }

  /**
   * 根据完整日志参数计算 bodyHash（ticket 申请与后端验签共用同一规则）
   * @param {Object} payload
   * @returns {Promise<string>}
   */
  function computeBodyHash(payload) {
    return sha256Hex(buildCanonicalPayloadString(payload));
  }

  /**
   * 将用户传入的原始字段规范化为符合接口要求的完整请求体
   *
   * @param {Object} input 用户传入的部分字段
   * @param {Object} config 实例配置（含 appId 等）
   * @returns {Object} 完整且已截断的日志体
   */
  function normalizePayload(input, config) {
    var now = Date.now();
    var payload = {
      /** appId — 应用标识，区分不同前端应用/模块，max 64 */
      appId: truncate(config.appId || input.appId || '', FIELD_LIMITS.appId),

      /** pageUrl — 发生错误时的页面 URL，max 1000 */
      pageUrl: truncate(
        input.pageUrl != null ? input.pageUrl : global.location.href,
        FIELD_LIMITS.pageUrl
      ),

      /** errorType — 错误分类，如 LoggerError / TypeError / UnhandledRejection，max 64 */
      errorType: truncate(input.errorType || 'UnknownError', FIELD_LIMITS.errorType),

      /** errorMessage — 错误摘要信息，max 2000 */
      errorMessage: truncate(input.errorMessage || '', FIELD_LIMITS.errorMessage),

      /** stack — 堆栈信息（可选，不参与 bodyHash），max 8000 */
      stack: truncate(input.stack || '', FIELD_LIMITS.stack),

      /** userAgent — 浏览器 UA（可选），max 1000 */
      userAgent: truncate(
        input.userAgent != null ? input.userAgent : global.navigator.userAgent || '',
        FIELD_LIMITS.userAgent
      ),

      /** deviceInfo — 设备/视口信息（可选），max 255，默认 "宽x高" */
      deviceInfo: truncate(
        input.deviceInfo != null ? input.deviceInfo : getDeviceInfo(),
        FIELD_LIMITS.deviceInfo
      ),

      /** timestamp — 客户端毫秒时间戳（参与 bodyHash） */
      timestamp: input.timestamp != null ? Number(input.timestamp) : now,

      /** retryCount — 重试次数 0–10（参与 bodyHash，可选） */
      retryCount: clampInt(input.retryCount, 0, 10, 0),
    };

    if (!payload.appId) {
      throw new Error('[FrontendLogReporter] appId is required');
    }
    if (!payload.errorMessage) {
      throw new Error('[FrontendLogReporter] errorMessage is required');
    }

    // 透传自定义字段（不参与 bodyHash，原样带给 /api/log）
    mergeExtraFields(payload, input);

    return payload;
  }

  /**
   * 将 input 中除标准字段外的自定义键合并到 payload（后端不校验时可扩展落盘）
   * @param {Object} payload
   * @param {Object} input
   */
  function mergeExtraFields(payload, input) {
    if (!input || typeof input !== 'object') return;
    for (var key in input) {
      if (!Object.prototype.hasOwnProperty.call(input, key)) continue;
      if (STANDARD_PAYLOAD_KEYS[key]) continue;
      payload[key] = input[key];
    }
  }

  /**
   * 判断接口业务是否成功（文档为 result=0，实际后端多为 result=1）
   * @param {*} result
   * @returns {boolean}
   */
  function isApiSuccess(result) {
    return result === 0 || result === 1;
  }

  /**
   * 解析接口 JSON 响应，统一抛出业务错误
   * @param {Response} response
   * @returns {Promise<Object>}
   */
  function parseJsonResponse(response) {
    return response.text().then(function (text) {
      var json;
      try {
        json = text ? JSON.parse(text) : {};
      } catch (e) {
        throw new Error('Invalid JSON response: HTTP ' + response.status);
      }
      if (!response.ok) {
        throw new Error(json.msg || 'HTTP ' + response.status);
      }
      if (!isApiSuccess(json.result)) {
        var err = new Error(json.msg || 'API error result=' + json.result);
        err.code = json.result;
        throw err;
      }
      return json;
    });
  }

  // ── 核心类 ────────────────────────────────────────────────────────────────

  /**
   * @param {Object} options
   * @param {string} options.appId     — 应用标识（必填）
   * @param {string} [options.baseUrl] — API 根地址，默认当前 origin
   * @param {boolean} [options.debug]  — 调试模式
   */
  function FrontendLogReporter(options) {
    this.config = Object.assign({}, DEFAULT_CONFIG, options || {});
    this.baseUrl = resolveBaseUrl(this.config.baseUrl);
    this.debug = this.config.debug === true;
    /** 跨域上报时使用 'omit'（如 Shopify 页 → s-api-eu.roborock.com） */
    this.credentials = this.config.credentials || 'same-origin';
    this.keepalive = this.config.keepalive === true;
  }

  FrontendLogReporter.prototype._log = function () {
    if (!this.debug) return;
    try {
      global.console.info.apply(global.console, ['[FrontendLogReporter]'].concat([].slice.call(arguments)));
    } catch (e) {}
  };

  /**
   * 第一步：申请短时 ticket
   *
   * POST /api/log/ticket
   * 请求体：{ bodyHash: "64位小写sha256" }
   * 成功响应 data：{ ticket: "xxxxx.yyyyy", expiredIn: 300 }
   * ticket 默认 5 分钟有效，且只能使用一次
   *
   * @param {string} bodyHash
   * @returns {Promise<{ticket: string, expiredIn: number}>}
   */
  FrontendLogReporter.prototype.requestTicket = function (bodyHash) {
    var url = this.baseUrl + '/api/log/ticket';
    this._log('requestTicket', url, bodyHash);

    return global
      .fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bodyHash: bodyHash }),
        credentials: this.credentials,
        mode: 'cors',
        keepalive: this.keepalive,
      })
      .then(parseJsonResponse)
      .then(function (json) {
        return json.data || {};
      });
  };

  /**
   * 第二步：携带 ticket 正式上报日志
   *
   * POST /api/log
   * 请求头：
   *   X-Log-Ticket    — ticket 接口返回的短时票据
   *   X-Log-Nonce     — 本次请求唯一随机串（5 分钟内不可复用）
   *   X-Log-Timestamp — 当前毫秒时间戳（与 body.timestamp 建议一致）
   * 请求体：完整日志字段（见 normalizePayload 注释）
   *
   * @param {Object} payload 已规范化的日志体
   * @param {string} ticket
   * @returns {Promise<void>}
   */
  FrontendLogReporter.prototype.submitLog = function (payload, ticket) {
    var url = this.baseUrl + '/api/log';
    var nonce = generateNonce();
    var requestTimestamp = String(payload.timestamp);

    this._log('submitLog', url, { ticket: ticket, nonce: nonce, payload: payload });

    return global
      .fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Log-Ticket': ticket,
          'X-Log-Nonce': nonce,
          'X-Log-Timestamp': requestTimestamp,
        },
        body: JSON.stringify(payload),
        credentials: this.credentials,
        mode: 'cors',
        keepalive: this.keepalive,
      })
      .then(parseJsonResponse);
  };

  /**
   * 完整上报流程：规范化 → bodyHash → 申请 ticket → 提交日志
   *
   * @param {Object} input 日志字段（errorType / errorMessage / stack 等）
   * @param {Object} [opts]
   * @param {number} [opts.maxRetries=0] 失败后最多重试次数（写入 retryCount）
   * @returns {Promise<void>}
   */
  FrontendLogReporter.prototype.report = function (input, opts) {
    var self = this;
    opts = opts || {};
    var maxRetries = clampInt(opts.maxRetries, 0, 10, 0);

    if (!this.baseUrl) {
      return Promise.reject(new Error('[FrontendLogReporter] baseUrl is empty'));
    }

    function attempt(retryCount) {
      var payload = normalizePayload(Object.assign({}, input, { retryCount: retryCount }), self.config);

      return computeBodyHash(payload)
        .then(function (bodyHash) {
          return self.requestTicket(bodyHash).then(function (ticketData) {
            var ticket = ticketData.ticket;
            if (!ticket) {
              throw new Error('ticket missing in response');
            }
            return self.submitLog(payload, ticket);
          });
        })
        .catch(function (err) {
          if (retryCount < maxRetries) {
            self._log('retry', retryCount + 1, err && err.message);
            return attempt(retryCount + 1);
          }
          throw err;
        });
    }

    return attempt(clampInt(input.retryCount, 0, 10, 0));
  };

  /**
   * 从 Error 对象快捷上报
   *
   * @param {Error|string} error
   * @param {Object} [extra] 额外字段（errorType / pageUrl / retryCount 等）
   * @returns {Promise<void>}
   */
  FrontendLogReporter.prototype.reportError = function (error, extra) {
    extra = extra || {};
    var message = '';
    var stack = '';
    var errorType = extra.errorType || 'Error';

    if (error instanceof Error) {
      message = error.message || String(error);
      stack = error.stack || '';
      errorType = extra.errorType || error.name || 'Error';
    } else {
      message = String(error);
    }

    return this.report(
      Object.assign({}, extra, {
        errorType: errorType,
        errorMessage: message,
        stack: extra.stack != null ? extra.stack : stack,
      })
    );
  };

  // ── 静态方法 ──────────────────────────────────────────────────────────────

  /**
   * 创建 reporter 实例
   * @param {Object} options
   * @returns {FrontendLogReporter}
   */
  FrontendLogReporter.create = function (options) {
    return new FrontendLogReporter(options);
  };

  /**
   * 使用全局配置的单例上报（需先设置 window.__FRONTEND_LOG_CONFIG__.appId）
   * @param {Object} input
   * @param {Object} [opts]
   * @returns {Promise<void>}
   */
  FrontendLogReporter.report = function (input, opts) {
    if (!global.__FRONTEND_LOG_REPORTER__) {
      global.__FRONTEND_LOG_REPORTER__ = new FrontendLogReporter();
    }
    return global.__FRONTEND_LOG_REPORTER__.report(input, opts);
  };

  /**
   * 使用全局配置的单例上报 Error
   * @param {Error|string} error
   * @param {Object} [extra]
   * @returns {Promise<void>}
   */
  FrontendLogReporter.reportError = function (error, extra) {
    if (!global.__FRONTEND_LOG_REPORTER__) {
      global.__FRONTEND_LOG_REPORTER__ = new FrontendLogReporter();
    }
    return global.__FRONTEND_LOG_REPORTER__.reportError(error, extra);
  };

  /**
   * 注册全局未捕获异常监听（window.error + unhandledrejection）
   * 同一页面仅安装一次
   *
   * @param {Object} [options] 传给 FrontendLogReporter 的配置
   * @returns {FrontendLogReporter}
   */
  FrontendLogReporter.installGlobalHandlers = function (options) {
    if (global.__FRONTEND_LOG_HANDLERS_INSTALLED__) {
      return global.__FRONTEND_LOG_REPORTER__ || new FrontendLogReporter(options);
    }
    global.__FRONTEND_LOG_HANDLERS_INSTALLED__ = true;

    var reporter = new FrontendLogReporter(options);
    global.__FRONTEND_LOG_REPORTER__ = reporter;

    global.addEventListener('error', function (event) {
      var target = event.target;
      // 资源加载错误（script/img 等）与 JS 运行时错误区分上报
      if (target && target !== global && (target.src || target.href)) {
        reporter
          .report({
            errorType: 'ResourceError',
            errorMessage:
              'Failed to load ' +
              (target.tagName || 'RESOURCE') +
              ': ' +
              (target.src || target.href || ''),
          })
          .catch(function () {});
        return;
      }

      reporter
        .reportError(event.error || event.message || 'Unknown script error', {
          errorType: event.error && event.error.name ? event.error.name : 'WindowError',
          pageUrl: event.filename ? event.filename + ':' + (event.lineno || 0) : undefined,
        })
        .catch(function () {});
    });

    global.addEventListener('unhandledrejection', function (event) {
      var reason = event.reason;
      if (reason instanceof Error) {
        reporter
          .reportError(reason, { errorType: 'UnhandledRejection' })
          .catch(function () {});
      } else {
        reporter
          .report({
            errorType: 'UnhandledRejection',
            errorMessage: String(reason),
          })
          .catch(function () {});
      }
    });

    return reporter;
  };

  // 暴露常量与底层方法，便于测试或自定义流程
  FrontendLogReporter.computeBodyHash = computeBodyHash;
  FrontendLogReporter.normalizePayload = normalizePayload;
  FrontendLogReporter.buildCanonicalPayloadString = buildCanonicalPayloadString;

  global.FrontendLogReporter = FrontendLogReporter;
})(typeof window !== 'undefined' ? window : globalThis);
